import { useEffect, useMemo, useState, type FormEvent } from 'react';

import { useChat } from '../../hooks/useChat';
import { api } from '../../services/api.js';
import type { Contact, ConversationType } from '../../types';
import {
  conversationTypeLabel,
  getChatCapabilities,
  getEffectiveConversationType,
} from '../../utils/conversation-capabilities';
import Avatar from './Avatar';
import ChatActionsMenu from './ChatActionsMenu';

export type Filter = 'all' | 'unread' | 'favorites' | 'family' | 'clients';

interface ChatListProps {
  conversationType?: ConversationType;
}

const RELATIONSHIP_CLIENT = new Set<Contact['relationshipType']>(['client', 'lead', 'vip']);

function conversationTypeListTitle(type: ConversationType | undefined): string {
  switch (type) {
    case 'direct':
      return 'Chats';
    case 'group':
      return 'Groepen';
    case 'community':
      return 'Communities';
    case 'channel':
      return 'Kanalen';
    case 'status':
      return 'Status';
    case 'broadcast':
      return 'Broadcast';
    case 'unknown':
      return 'Overig';
    default:
      return 'Chats';
  }
}

const ChatList = ({ conversationType = 'direct' }: ChatListProps) => {
  const { chats, activeChat, selectChat, createChat, toggleFavorite, loading } = useChat();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const list = await api.contacts();
        if (active) setContacts(list);
      } catch {
        // ignore; list still works without category mapping
      }
    };
    void load();
    const reFetch = () => {
      void load();
    };
    window.addEventListener('wabrain-contacts-updated', reFetch);
    return () => {
      active = false;
      window.removeEventListener('wabrain-contacts-updated', reFetch);
    };
  }, []);

  const contactByChat = useMemo(() => {
    const map = new Map<string, Contact>();
    for (const contact of contacts) {
      map.set(`${contact.accountId}:${contact.phone}`, contact);
    }
    return map;
  }, [contacts]);

  const filters = useMemo(() => {
    const base = [
      { key: 'all' as Filter, label: 'Alles' },
      { key: 'unread' as Filter, label: 'Ongelezen' },
      { key: 'favorites' as Filter, label: 'Favorieten' },
    ];
    if (conversationType === 'direct') {
      base.push(
        { key: 'family' as Filter, label: 'Familie' },
        { key: 'clients' as Filter, label: 'Klanten' },
      );
    }
    return base;
  }, [conversationType]);

  const unreadCount = useMemo(
    () => chats.reduce((sum, chat) => sum + (chat.unreadCount ?? 0), 0),
    [chats],
  );

  const visibleChats = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('nl-NL');
    return chats.filter((chat) => {
      if (getEffectiveConversationType(chat) !== conversationType) return false;

      if (filter === 'unread' && !(chat.unreadCount && chat.unreadCount > 0)) return false;
      if (filter === 'favorites' && !chat.favorite) return false;
      if (filter === 'family' || filter === 'clients') {
        const identifier = chat.phone ?? chat.externalId;
        const relationship = identifier
          ? contactByChat.get(`${chat.accountId}:${identifier}`)?.relationshipType
          : undefined;
        if (filter === 'family' && relationship !== 'family') return false;
        if (filter === 'clients' && (!relationship || !RELATIONSHIP_CLIENT.has(relationship))) return false;
      }
      if (normalized) {
        return (
          chat.title.toLocaleLowerCase('nl-NL').includes(normalized) ||
          chat.phone?.includes(normalized)
        );
      }
      return true;
    });
  }, [chats, query, filter, contactByChat, conversationType]);

  // Clear the selected chat when it no longer matches the active filter/view.
  useEffect(() => {
    if (!activeChat) return;
    const stillVisible = visibleChats.some((chat) => chat.id === activeChat.id);
    if (!stillVisible) {
      selectChat(null);
    }
  }, [visibleChats, activeChat, selectChat]);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const chat = await createChat(title, phone || undefined);
      setTitle('');
      setPhone('');
      setShowCreate(false);
      selectChat(chat.id);
    } catch {
      setCreateError('De chat kon niet worden aangemaakt.');
    } finally {
      setCreating(false);
    }
  };

  const capabilities = getChatCapabilities({ conversationType });

  return (
    <aside className={`chat-list-panel ${activeChat ? 'has-selection' : ''}`} aria-label={conversationTypeListTitle(conversationType)}>
      <header className="panel-header">
        <div>
          <p className="eyebrow">Inbox</p>
          <h2>{conversationTypeListTitle(conversationType)}</h2>
        </div>
        {capabilities.canSend && (
          <button
            type="button"
            className="button icon"
            aria-label="Nieuwe chat"
            onClick={() => setShowCreate((visible) => !visible)}
          >
            +
          </button>
        )}
      </header>
      {showCreate && capabilities.canSend && (
        <form className="create-chat-form" onSubmit={handleCreate}>
          <label>
            Contactnaam
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={200}
              disabled={creating}
              required
            />
          </label>
          <label>
            Telefoonnummer (E.164)
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="+31612345678"
              pattern="^\\+[1-9][0-9]{6,14}$"
              disabled={creating}
              required
            />
          </label>
          {createError && (
            <p className="notice error" role="alert">
              {createError}
            </p>
          )}
          <div className="button-row">
            <button className="button primary small" disabled={creating}>
              {creating ? 'Bezig…' : 'Aanmaken'}
            </button>
            <button
              type="button"
              className="button secondary small"
              onClick={() => setShowCreate(false)}
            >
              Annuleren
            </button>
          </div>
        </form>
      )}
      <div className="chat-filters" role="tablist" aria-label="Chat filters">
        {filters.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={filter === item.key}
            className={`chat-filter-tab ${filter === item.key ? 'active' : ''}`}
            onClick={() => setFilter(item.key)}
          >
            {item.label}
            {item.key === 'unread' && unreadCount > 0 && (
              <span className="filter-badge">{unreadCount}</span>
            )}
          </button>
        ))}
      </div>
      <div className="search-field">
        <span aria-hidden="true">⌕</span>
        <label className="sr-only" htmlFor="chat-search">
          {conversationTypeListTitle(conversationType)} doorzoeken
        </label>
        <input
          id="chat-search"
          type="search"
          placeholder={`${conversationTypeListTitle(conversationType)} doorzoeken`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="chat-items" aria-live="polite">
        {loading && chats.length === 0 && <p className="empty-state">{conversationTypeListTitle(conversationType)} laden…</p>}
        {!loading && visibleChats.length === 0 && (
          <p className="empty-state">
            {query
              ? `Geen ${conversationTypeLabel(conversationType).toLowerCase()} gevonden.`
              : `Nog geen ${conversationTypeLabel(conversationType).toLowerCase()}.`}
          </p>
        )}
        {visibleChats.map((chat) => {
          const isUnread = (chat.unreadCount ?? 0) > 0;
          const lastMsg = chat.lastMessage;
          const isOutbound = lastMsg?.direction === 'outbound';
          const statusIcon = isOutbound
            ? lastMsg?.status === 'read'
              ? '✓✓'
              : lastMsg?.status === 'delivered'
                ? '✓✓'
                : lastMsg?.status === 'sent'
                  ? '✓'
                  : ''
            : '';
          const chatCapabilities = getChatCapabilities(chat);
          return (
            <div
              key={chat.id}
              role="button"
              tabIndex={0}
              className={`chat-item ${activeChat?.id === chat.id ? 'active' : ''} ${isUnread ? 'has-unread' : ''}`}
              onClick={() => selectChat(chat.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectChat(chat.id);
                }
              }}
              aria-current={activeChat?.id === chat.id ? 'page' : undefined}
            >
              <Avatar chatId={chat.id} title={chat.title} size={44} />
              <span className="chat-summary">
                <span className="chat-title-row">
                  <strong>{chat.title}</strong>
                  <time dateTime={chat.updatedAt}>
                    {formatListTime(chat.updatedAt)}
                  </time>
                </span>
                <span className="chat-meta-row">
                  <span className="chat-preview">
                    {isOutbound && statusIcon && (
                      <span className={`msg-status ${lastMsg?.status}`}>{statusIcon}</span>
                    )}
                    {chat.lastMessage?.content ||
                      (chat.lastMessage?.media ? 'Bijlage' : 'Nog geen berichten')}
                  </span>
                  <span className="chat-meta-right">
                    <button
                      type="button"
                      className={`fav-toggle ${chat.favorite ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggleFavorite(chat.id);
                      }}
                      aria-label={chat.favorite ? 'Uit favorieten verwijderen' : 'Aan favorieten toevoegen'}
                    >
                      {chat.favorite ? '★' : '☆'}
                    </button>
                    <ChatActionsMenu chat={chat} />
                    {isUnread && (
                      <span className="unread-badge">{chat.unreadCount}</span>
                    )}
                    {chatCapabilities.canChangeConsent && (
                      <span
                        className={`consent-dot ${chat.consent}`}
                        aria-label={`Toestemming: ${chat.consent}`}
                      />
                    )}
                  </span>
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

const formatListTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfDate.getTime()) / (24 * 60 * 60 * 1_000));

  if (diffDays === 0) {
    return new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' }).format(date);
  }
  if (diffDays === 1) {
    return 'Gisteren';
  }
  if (diffDays > 1 && diffDays < 7) {
    return new Intl.DateTimeFormat('nl-NL', { weekday: 'long' }).format(date);
  }
  return new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

export default ChatList;
